import { memo, Fragment } from "react";
import PropTypes from "prop-types";
import InfiniteScroll from "react-infinite-scroll-component";
import { Skeleton } from "antd";
import Row from "antd/lib/row";
import { useDispatch } from "react-redux";
import moment from "moment";
import _get from "lodash/get";
import {
  triggerModalSetTag,
  createTagRequest,
} from "providers/MessengerProvider/slice";
import { setPopup } from "providers/GeneralProvider/slice";
import ConversationCell from "./ConversationCell";
import "./styles.less";

// Skeleton component for loading state
const ConversationSkeleton = () => (
  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid #f0f0f0' }}>
    <Skeleton.Avatar active size={40} />
    <div style={{ flex: 1 }}>
      <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: 180 }} />
    </div>
  </div>
);

// Month separator component
const MonthSeparator = ({ month, year }) => (
  <div style={{
    padding: '8px 16px',
    background: '#f5f5f5',
    fontSize: '12px',
    color: '#666',
    fontWeight: 500,
    borderBottom: '1px solid #e8e8e8',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }}>
    Tháng {month}/{year}
  </div>
);

// Helper to get month/year key
const getMonthYearKey = (date) => {
  if (!date) return null;
  return moment(date).format('MM-YYYY');
};

const ConversationList = ({
  conversationIds,
  conversationEntities,
  onConversationCellClick,
  activeConversationId,
  getConversationList,
  hasMore = true,
  currentUserId,
  isGettingList = false,
}) => {
  const dispatch = useDispatch();

  const _onDeleteTag = (data) => {
    dispatch(
      setPopup({
        isOpen: true,
        data: {
          title: "Xoá tag",
          content: "Bạn có muốn xoá tag cho người dùng này?",
          okText: "OK",
          cancelText: "Huỷ",
          onOk: () => {
            dispatch(
              createTagRequest({
                conversationId: data._id,
                name: " ",
                color: "blue",
              })
            );
          },
        },
      })
    );
  };

  // Wrapper to prevent duplicate requests when already loading
  const handleLoadMore = () => {
    if (!isGettingList && hasMore) {
      getConversationList();
    }
  };

  // Show initial loading skeleton
  if (isGettingList && conversationIds.length === 0) {
    return (
      <div className="conversation-list-container" id="scrollable-conversation-list">
        <ConversationSkeleton />
        <ConversationSkeleton />
        <ConversationSkeleton />
        <ConversationSkeleton />
        <ConversationSkeleton />
      </div>
    );
  }

  // Show empty state when not loading and no data
  if (!isGettingList && conversationIds.length === 0) {
    return (
      <div className="conversation-list-container" id="scrollable-conversation-list">
        <Row style={{ padding: 24 }} justify="center">
          <span style={{ color: '#999', fontSize: '14px' }}>Không có cuộc hội thoại</span>
        </Row>
      </div>
    );
  }

  return (
    <div
      className="conversation-list-container"
      id="scrollable-conversation-list"
    >
      <InfiniteScroll
        scrollThreshold="100px"
        dataLength={conversationIds.length}
        next={handleLoadMore}
        hasMore={hasMore}
        loader={null}
        scrollableTarget="scrollable-conversation-list"
        endMessage={
          conversationIds.length > 0 && !hasMore && !isGettingList ? (
            <Row style={{ padding: 8 }} justify="center">
              <span style={{ color: '#999', fontSize: '12px' }}>Đã tải hết</span>
            </Row>
          ) : null
        }
      >
        {conversationIds.map((id, index) => {
          const conversation = conversationEntities[id];
          const currentDate = _get(conversation, 'latestMessage.createdAt') || _get(conversation, 'updatedAt');
          const currentMonthKey = getMonthYearKey(currentDate);

          // Check if we need to show month separator
          let showSeparator = false;
          if (index === 0) {
            showSeparator = true;
          } else {
            const prevId = conversationIds[index - 1];
            const prevConversation = conversationEntities[prevId];
            const prevDate = _get(prevConversation, 'latestMessage.createdAt') || _get(prevConversation, 'updatedAt');
            const prevMonthKey = getMonthYearKey(prevDate);
            showSeparator = currentMonthKey !== prevMonthKey;
          }

          const monthYear = currentDate ? moment(currentDate).format('M/YYYY').split('/') : null;

          return (
            <Fragment key={id}>
              {showSeparator && monthYear && (
                <MonthSeparator month={monthYear[0]} year={monthYear[1]} />
              )}
              <ConversationCell
                className={activeConversationId === id ? "active-conversation" : ""}
                onClick={() => onConversationCellClick(id)}
                data={conversation}
                currentUserId={currentUserId}
                onClickTag={(data) => {
                  dispatch(
                    triggerModalSetTag({
                      visible: true,
                      data,
                    })
                  );
                }}
                onDeleteTag={_onDeleteTag}
              />
            </Fragment>
          );
        })}
        {/* Loading indicator when fetching more */}
        {isGettingList && conversationIds.length > 0 && (
          <div>
            <ConversationSkeleton />
            <ConversationSkeleton />
            <ConversationSkeleton />
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};

ConversationList.propTypes = {
  conversationIds: PropTypes.array.isRequired,
  conversationEntities: PropTypes.object.isRequired,
  onConversationCellClick: PropTypes.func.isRequired,
  activeConversationId: PropTypes.string.isRequired,
  getConversationList: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  currentUserId: PropTypes.string.isRequired,
  isGettingList: PropTypes.bool,
};

export default memo(ConversationList);
